// ============================================
// PAGE - Espera de Confirmación de Pago
// ============================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from '@almacenes/ui';
import { 
  useAuthStore, 
  flowRepository, 
  type SubscriptionStatus,
} from '@almacenes/core';
import { useQueryClient } from '@tanstack/react-query';
import logo from '@/assets/logos/logo.svg';

export const SuscripcionEsperaPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { session: cachedSession, logout } = useAuthStore();
  
  // Estados para polling de confirmación de pago
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>('PENDING');
  const [pollingStartTime] = useState<number>(Date.now());
  const [isPollingTimeout, setIsPollingTimeout] = useState(false);
  const [currentTime, setCurrentTime] = useState<number>(Date.now());

  // Refs para evitar múltiples llamadas
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Obtener RUT de la URL
  const rutAlmacen = searchParams.get('rut') || cachedSession?.almacen?.rut;

  // Actualizar el tiempo cada segundo
  useEffect(() => {
    timeUpdateIntervalRef.current = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  // Función para detener el polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Función para verificar el estado de la suscripción
  const checkSubscriptionStatus = async () => {
    if (!rutAlmacen) {
      console.error('[SuscripcionEsperaPage] No hay RUT para consultar estado');
      return;
    }

    try {
      console.log('[SuscripcionEsperaPage] Consultando estado de suscripción...');
      const statusResponse = await flowRepository.getSubscriptionStatus(rutAlmacen);
      console.log('[SuscripcionEsperaPage] Estado de suscripción:', statusResponse.status);

      setSubscriptionStatus(statusResponse.status);

      // Si el estado es ACCEPTED, detener polling e invalida sesión
      if (statusResponse.status === 'ACCEPTED') {
        console.log('[SuscripcionEsperaPage] ¡Pago confirmado! Redirigiendo al dashboard...');
        stopPolling();
        
        // Invalidar la sesión para que se recargue con hasPremiumAccess = true
        await queryClient.invalidateQueries({ queryKey: ['session'] });
        
        // Pequeño delay para asegurar que la sesión se actualice
        setTimeout(() => {
          navigate('/dashboard');
        }, 500);
      }

      // Verificar si ha pasado el timeout de 30 minutos
      if (Date.now() - pollingStartTime > 30 * 60 * 1000) {
        console.log('[SuscripcionEsperaPage] Timeout de 30 minutos alcanzado');
        stopPolling();
        setIsPollingTimeout(true);
      }
    } catch (error) {
      console.error('[SuscripcionEsperaPage] Error consultando estado de suscripción:', error);
    }
  };

  // Función para iniciar el polling
  const startPolling = () => {
    // Limpiar cualquier polling anterior
    stopPolling();

    // Primera consulta inmediata
    checkSubscriptionStatus();

    // Configurar polling cada 5 segundos
    pollingIntervalRef.current = setInterval(() => {
      checkSubscriptionStatus();
    }, 5000);
  };

  // Iniciar polling al montar el componente
  useEffect(() => {
    if (!rutAlmacen) {
      console.error('[SuscripcionEsperaPage] No hay RUT, redirigiendo a suscripción');
      navigate('/suscripcion');
      return;
    }

    startPolling();

    return () => {
      stopPolling();
    };
  }, [rutAlmacen]);

  const handleBackToPaymentOptions = () => {
    stopPolling();
    navigate('/suscripcion');
  };

  // Formatear tiempo transcurrido
  const getElapsedTime = (): string => {
    const elapsed = Math.floor((currentTime - pollingStartTime) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes}m ${seconds}s`;
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F3F4F9' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
        <img 
          src={logo} 
          alt="Almacenes Digitales" 
          className="h-9"
        />
        <Button
          variant="danger"
          onClick={handleLogout}
          size="sm"
        >
          Cerrar sesión
        </Button>
      </div>

      {/* Contenido */}
      <div className="flex-1 flex items-center justify-center px-8 py-4">
        <div className="max-w-3xl w-full">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-center max-w-3xl">
              {isPollingTimeout ? (
                // Timeout - Mostrar mensaje de límite excedido
                <>
                  <div className="text-8xl mb-8">⏱️</div>
                  <h1 className="text-4xl font-bold mb-6" style={{ color: '#11194F' }}>
                    Tiempo de Espera Excedido
                  </h1>
                  <p className="text-gray-600 text-xl mb-8 leading-relaxed">
                    Han pasado más de 30 minutos y no hemos recibido la confirmación del pago.
                  </p>
                  <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-6 mb-8">
                    <p className="text-yellow-800 font-semibold mb-2">
                      ⚠️ ¿Qué puedo hacer?
                    </p>
                    <p className="text-yellow-700 text-sm mb-4">
                      Puedes volver a intentar el proceso de pago. Si el problema persiste, contacta con soporte.
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleBackToPaymentOptions}
                  >
                    Volver a Opciones de Pago
                  </Button>
                </>
              ) : (
                // Esperando confirmación - Mostrar spinner y status
                <>
                  <h1 className="text-4xl font-bold mb-6" style={{ color: '#11194F' }}>
                    Revisa tu Correo Electrónico
                  </h1>
                  <p className="text-gray-600 text-xl mb-8 leading-relaxed">
                    Te hemos enviado un correo con las instrucciones para completar el pago.
                  </p>
                  
                  {/* Información del proceso */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8 text-left">
                    <p className="text-blue-800 font-semibold mb-4 text-center">
                      💡 ¿Cómo funciona?
                    </p>
                    <ul className="space-y-3 text-blue-700 text-sm">
                      <li className="flex items-start gap-3">
                        <span className="text-lg">1️⃣</span>
                        <span>
                          <strong>Si tienes una tarjeta registrada:</strong> El cargo se realizará automáticamente. Solo espera la confirmación.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-lg">2️⃣</span>
                        <span>
                          <strong>Si NO tienes tarjeta registrada:</strong> Recibirás un correo de Flow con un link para completar el pago.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="text-lg">3️⃣</span>
                        <span>
                          Una vez confirmado el pago, serás redirigido automáticamente al dashboard con acceso premium activado.
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Status y spinner */}
                  <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-200">
                    <div className="flex flex-col items-center justify-center mb-6">
                      <Spinner size="lg" />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between px-6 py-3 bg-blue-50 rounded-lg">
                        <span className="text-gray-700 font-semibold">Estado:</span>
                        <span className="text-blue-600 font-bold">
                          {subscriptionStatus === 'PENDING' ? 'Esperando Confirmación...' : 'Confirmado ✓'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between px-6 py-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700 font-semibold">Tiempo transcurrido:</span>
                        <span className="text-gray-600 font-mono">{getElapsedTime()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Información adicional */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                    <p className="text-gray-700 text-sm">
                      Por favor, mantenga esta ventana abierta. Estamos consultando el estado y el proceso finalizará automáticamente cuando recibamos la confirmación.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
